/* import __COLOCATED_TEMPLATE__ from './custom-answer-settings.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';

interface Args {
  aiAgentSettings: any;
  conversationImports: any;
}

export default class AiAgentCustomAnswerSettings extends Component<Args> {
  @service declare appService: AppService;
  @service declare router: any;
  @service declare permissionsService: any;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare modalService: any;
  @service declare intercomEventService: any;
  @service declare intercomConfirmService: IntercomConfirmService;

  @tracked showAiAgentImportSettingsModal = false;

  analytics = {
    section: 'ai-chatbot',
    place: 'settings.custom-answers',
  };

  get titleToken(): string {
    return this.intl.t('ai-agent.settings.custom-answer-settings-title');
  }

  get app() {
    return this.appService.app;
  }

  get pricing5CustomerWithSuggestionsTurnedOn() {
    if (!this.appService.app.canUseFeature('show-fin-suggestion-settings-to-p5-customers')) {
      return false;
    }

    let settings = this.args.aiAgentSettings;

    if (settings.hasDirtyAttributes) {
      return true;
    }

    return (
      this.appService.app.usesResolutionsWithCustomAnswers &&
      (settings.contentSuggestionsEnabledForUsers ||
        settings.contentSuggestionsEnabledForVisitors ||
        settings.predictiveAnswersEnabledForUsers ||
        settings.predictiveAnswersEnabledForVisitors)
    );
  }

  get hasActiveZendeskChatConversationImport() {
    return this.args.conversationImports?.any(
      (conversationImport: any) =>
        conversationImport.source === 'zendesk_chat' && conversationImport.status === 'in_progress',
    );
  }

  get hasActiveZendeskTicketsConversationImport() {
    return this.args.conversationImports?.any(
      (conversationImport: any) =>
        conversationImport.source === 'zendesk_tickets' &&
        conversationImport.status === 'in_progress',
    );
  }

  @action completeConversationImportFlow() {
    this.modalService.closeModal();
    this.router.refresh();
  }

  @action openAiAgentImportSettingsModal() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'import_conversation_history_link',
      context: 'zendesk_chat_import',
    });
    this.showAiAgentImportSettingsModal = true;
  }

  @action closeAiAgentImportSettingsModal() {
    this.showAiAgentImportSettingsModal = false;
  }

  @action sectionDidOpen(sectionName: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      context: 'operator_settings',
      object: `${sectionName}-section`,
    });
  }

  @action sectionDidClose(sectionName: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'closed',
      context: 'operator_settings',
      object: `${sectionName}-section`,
    });
  }

  @action
  shouldShowConfirmBeforeSave() {
    return (
      this.appService.app.usesResolutionsWithCustomAnswers &&
      !this.args.aiAgentSettings.contentSuggestionsEnabledForUsers &&
      !this.args.aiAgentSettings.contentSuggestionsEnabledForVisitors &&
      !this.args.aiAgentSettings.predictiveAnswersEnabledForUsers &&
      !this.args.aiAgentSettings.predictiveAnswersEnabledForVisitors
    );
  }

  @action
  async confirmBeforeSaving() {
    if (!this.shouldShowConfirmBeforeSave()) {
      return true;
    }

    let confirmOptions = {
      title: this.intl.t('ai-agent.settings.custom-answer-suggestions.disable-modal.title'),
      body: this.intl.t('ai-agent.settings.custom-answer-suggestions.disable-modal.body'),
      confirmButtonText: this.intl.t(
        'ai-agent.settings.custom-answer-suggestions.disable-modal.confirm',
      ),
      cancelButtonText: this.intl.t(
        'ai-agent.settings.custom-answer-suggestions.disable-modal.cancel',
      ),
      primaryButtonType: 'primary-destructive',
    };

    let confirmed = await this.intercomConfirmService.confirm(confirmOptions);
    if (!confirmed) {
      this.args.aiAgentSettings.rollbackAttributes();
      throw new Error();
    }
    return true;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::CustomAnswerSettings': typeof AiAgentCustomAnswerSettings;
    'ai-agent/custom-answer-settings': typeof AiAgentCustomAnswerSettings;
  }
}
